import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, CreditCard, Calendar, IndianRupee, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVERAGE_TYPES = ['Health', 'Life', 'Top-up'];

const Insurance = ({ baby }) => {
  const navigate = useNavigate();
  const { viewOnlyMode } = useViewOnly();
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [formData, setFormData] = useState({
    provider_name: '',
    policy_number: '',
    group_policy: '',
    coverage_type: '',
    effective_date: '',
    expiry_date: '',
    sum_insured: '',
    notes: ''
  });

  useEffect(() => {
    if (baby?.id) {
      fetchInsurances();
    }
  }, [baby]);

  const fetchInsurances = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/insurance/${baby.id}`);
      // Sort by effective date (newest first)
      const sorted = response.data.sort((a, b) => {
        const dateA = parseDate(a.effective_date);
        const dateB = parseDate(b.effective_date);
        return dateB - dateA;
      });
      setInsurances(sorted);
    } catch (error) {
      console.error('Error fetching insurances:', error);
      toast.error('Failed to fetch insurance records');
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const [day, month, year] = dateStr.split('/');
    return new Date(year, month - 1, day);
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Handle date input with auto-advance (DD/MM/YYYY format)
  const handleDateInput = (fieldName, e) => {
    let value = e.target.value;
    
    // Allow backspace/delete to work naturally
    if (e.nativeEvent.inputType === 'deleteContentBackward' || e.nativeEvent.inputType === 'deleteContentForward') {
      setFormData({
        ...formData,
        [fieldName]: value
      });
      return;
    }
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData({
      ...formData,
      [fieldName]: value
    });
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = parseDate(expiryDate);
    return expiry < new Date();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate date format (DD/MM/YYYY)
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateRegex.test(formData.effective_date)) {
      toast.error('Effective date must be in DD/MM/YYYY format');
      return;
    }

    if (formData.expiry_date && !dateRegex.test(formData.expiry_date)) {
      toast.error('Expiry date must be in DD/MM/YYYY format');
      return;
    }

    // Validate dates
    if (formData.expiry_date) {
      const effective = parseDate(formData.effective_date);
      const expiry = parseDate(formData.expiry_date);
      if (expiry <= effective) {
        toast.error('Expiry date must be after effective date');
        return;
      }
    }

    try {
      const insuranceData = {
        baby_id: baby.id,
        provider_name: formData.provider_name,
        policy_number: formData.policy_number,
        group_policy: formData.group_policy || undefined,
        coverage_type: formData.coverage_type,
        effective_date: formData.effective_date,
        expiry_date: formData.expiry_date || undefined,
        sum_insured: formData.sum_insured ? parseFloat(formData.sum_insured) : undefined,
        notes: formData.notes || undefined
      };

      if (editingInsurance) {
        await axios.put(`${API}/insurance/${editingInsurance.id}`, insuranceData);
        toast.success('Insurance updated successfully!');
      } else {
        await axios.post(`${API}/insurance`, insuranceData);
        toast.success('Insurance added successfully!');
      }

      setShowDialog(false);
      resetForm();
      fetchInsurances();
    } catch (error) {
      console.error('Error saving insurance:', error);
      toast.error('Failed to save insurance');
    }
  };

  const handleEdit = (insurance) => {
    setEditingInsurance(insurance);
    setFormData({
      provider_name: insurance.provider_name,
      policy_number: insurance.policy_number,
      group_policy: insurance.group_policy || '',
      coverage_type: insurance.coverage_type,
      effective_date: insurance.effective_date,
      expiry_date: insurance.expiry_date || '',
      sum_insured: insurance.sum_insured || '',
      notes: insurance.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this insurance record?')) {
      return;
    }

    try {
      await axios.delete(`${API}/insurance/${id}`);
      toast.success('Insurance deleted successfully!');
      fetchInsurances();
    } catch (error) {
      console.error('Error deleting insurance:', error);
      toast.error('Failed to delete insurance');
    }
  };

  const resetForm = () => {
    setFormData({
      provider_name: '',
      policy_number: '',
      group_policy: '',
      coverage_type: '',
      effective_date: '',
      expiry_date: '',
      sum_insured: '',
      notes: ''
    });
    setEditingInsurance(null);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 shadow-md">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div>
            <button 
              onClick={() => navigate('/')} 
              className="text-white hover:text-purple-100 mb-2 text-sm"
            >
              ← Back to Dashboard
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Insurance
            </h1>
            <p className="text-purple-100 text-sm mt-1">
              Manage {baby?.name}'s insurance policies
            </p>
          </div>
          {!viewOnlyMode && (
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-white text-purple-600 hover:bg-purple-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Insurance
            </Button>
          )}
        </div>
      </div>

      {/* Insurance Cards */}
      <div className="max-w-6xl mx-auto p-6">
        {loading ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading insurance records...</p>
          </div>
        ) : insurances.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No insurance policies added yet</p>
            {!viewOnlyMode && (
              <p className="text-gray-400 text-sm mt-2">Click "Add Insurance" to get started</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {insurances.map((insurance) => {
              const expired = isExpired(insurance.expiry_date);
              return (
                <div 
                  key={insurance.id} 
                  className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 ${expired ? 'border-2 border-red-200' : ''}`}
                >
                  {expired && (
                    <div className="bg-red-100 text-red-700 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-3">
                      Expired
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-1">{insurance.provider_name}</h3>
                      <p className="text-sm text-gray-500">Policy: {insurance.policy_number}</p>
                    </div>
                    {!viewOnlyMode && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(insurance)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(insurance.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">Coverage:</span>
                      <span className="font-medium text-gray-800">{insurance.coverage_type}</span>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Effective:</span>
                        <span className="font-medium text-gray-800">{insurance.effective_date}</span>
                      </div>

                      {insurance.expiry_date && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">|</span>
                          <span className="text-gray-600">Expires:</span>
                          <span className={`font-medium ${expired ? 'text-red-600' : 'text-gray-800'}`}>
                            {insurance.expiry_date}
                          </span>
                        </div>
                      )}
                    </div>

                    {insurance.sum_insured && (
                      <div className="flex items-center gap-2 text-sm">
                        <IndianRupee className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Sum Insured:</span>
                        <span className="font-medium text-gray-800">
                          ₹{insurance.sum_insured.toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInsurance ? 'Edit Insurance' : 'Add Insurance'}</DialogTitle>
            <DialogDescription>
              {editingInsurance ? 'Update insurance policy details' : 'Add a new insurance policy for your baby'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="provider_name">Provider Name *</Label>
                <Input
                  id="provider_name"
                  value={formData.provider_name}
                  onChange={(e) => setFormData({...formData, provider_name: e.target.value})}
                  placeholder="e.g., Star Health Insurance"
                  required
                />
              </div>

              <div>
                <Label htmlFor="policy_number">Policy Number *</Label>
                <Input
                  id="policy_number"
                  value={formData.policy_number}
                  onChange={(e) => setFormData({...formData, policy_number: e.target.value})}
                  placeholder="e.g., POL123456"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="group_policy">Group Policy (Optional)</Label>
                <Input
                  id="group_policy"
                  value={formData.group_policy}
                  onChange={(e) => setFormData({...formData, group_policy: e.target.value})}
                  placeholder="e.g., GRP789"
                />
              </div>

              <div>
                <Label htmlFor="coverage_type">Coverage Type *</Label>
                <Select
                  value={formData.coverage_type}
                  onValueChange={(value) => setFormData({...formData, coverage_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select coverage type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COVERAGE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="effective_date">Effective Date *</Label>
                <div className="relative">
                  <Input
                    id="effective_date"
                    value={formData.effective_date}
                    onChange={(e) => handleDateInput('effective_date', e)}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    required
                  />
                  <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <Label htmlFor="expiry_date">Expiry Date (Optional)</Label>
                <div className="relative">
                  <Input
                    id="expiry_date"
                    value={formData.expiry_date}
                    onChange={(e) => handleDateInput('expiry_date', e)}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                  <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="sum_insured">Sum Insured ₹ (Optional)</Label>
              <Input
                id="sum_insured"
                type="number"
                value={formData.sum_insured}
                onChange={(e) => setFormData({...formData, sum_insured: e.target.value})}
                placeholder="e.g., 500000"
                min="0"
                step="1"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about this insurance policy"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editingInsurance ? 'Update' : 'Add'} Insurance
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Insurance;
